import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import type { Feature, Polygon } from 'geojson';

import { useT } from '../i18n';
import { ROMANIA_BOUNDS, ROMANIA_CENTER, ROMANIA_DEFAULT_ZOOM, polygonFromLatLngs } from '../lib/geo';
import type { GeocodeResult } from '../lib/nominatim';
import './ParcelMap.css';

export type MapMode = 'idle' | 'drawing' | 'editing';
export type BaseLayerId = 'satellite' | 'street';

export interface ParcelMapHandle {
  startDraw: () => void;
  stopDraw: () => void;
  startEdit: () => void;
  stopEdit: () => void;
  reset: () => void;
  flyToResult: (result: GeocodeResult) => void;
  invalidate: () => void;
}

interface ParcelMapProps {
  /**
   * A parcel already in the wizard's state, drawn on mount.
   *
   * The map is destroyed whenever step 1 unmounts, so without this the owner
   * loses their outline the moment they visit step 2 and come back. Read once,
   * at mount — this is not a controlled value, and re-rendering the parent must
   * never redraw the shape under the owner's cursor.
   */
  initialPolygon?: Feature<Polygon> | null;
  onPolygonChange: (polygon: Feature<Polygon> | null) => void;
  onModeChange: (mode: MapMode) => void;
}

/*
 * Basemaps — two providers, chosen at build time by which key is present.
 *
 * MAPBOX is the intended provider. Its free allowance (200k styled tile
 * requests/month) is far above what this form generates, and unlike Esri,
 * MapTiler and the OSM community tiles, it permits commercial use. Set
 * VITE_MAPBOX_TOKEN and it is used for both layers.
 *
 * STADIA is the fallback, and only because it is what the app shipped with.
 * It serves tiles unkeyed from localhost, so development works with no key at
 * all — but on a real domain, without VITE_STADIA_API_KEY, every tile returns
 * 401 and the map goes blank. Its free tier is also non-commercial.
 *
 * So: with a Mapbox token, this works in production. Without one, it works
 * only on localhost. Do not deploy on the fallback.
 */
const MAPBOX_TOKEN = (import.meta.env.VITE_MAPBOX_TOKEN as string | undefined) ?? '';

/*
 * Style ids, in `owner/style-id` form.
 *
 * The defaults are Mapbox's own. Both are overridable from .env so a style
 * designed in Mapbox Studio can be dropped in without touching code — set
 * VITE_MAPBOX_STYLE_STREET to e.g. `mariodan/clx123abc` and it is used as-is.
 *
 * A custom style is the ONLY way to get Romanian labels: these tiles arrive
 * pre-rendered, so the language is baked in at publish time and cannot be
 * changed by a URL parameter. Mapbox's stock styles label in English, which is
 * why the opening country view reads "Romania" rather than "România".
 *
 * streets-v12       — the closest stock match to a Google-style street map,
 *                     with the dense place and street labels that make a
 *                     parcel findable. `mapbox/light-v11` is quieter and
 *                     nearer the brand, at the cost of far fewer labels.
 * satellite-streets — imagery WITH roads and names baked in. Plain
 *                     `mapbox/satellite-v9` is imagery only; on bare imagery
 *                     it is very hard to tell which field is yours.
 */
const MAPBOX_STREET_STYLE =
  (import.meta.env.VITE_MAPBOX_STYLE_STREET as string | undefined) || 'mapbox/streets-v12';
const MAPBOX_SATELLITE_STYLE =
  (import.meta.env.VITE_MAPBOX_STYLE_SATELLITE as string | undefined) || 'mapbox/satellite-streets-v12';

/*
 * 512px tiles at @2x. Leaflet assumes 256px, so `tileSize: 512` must be paired
 * with `zoomOffset: -1` or every zoom level is off by one and the map lands in
 * the wrong place. 512 is also half the requests of 256 for the same view,
 * which is the difference between comfortably inside the free tier and not.
 */
const mapboxTileUrl = (style: string) =>
  `https://api.mapbox.com/styles/v1/${style}/tiles/512/{z}/{x}/{y}@2x?access_token=${MAPBOX_TOKEN}`;

const MAPBOX_TILE_OPTIONS: L.TileLayerOptions = {
  tileSize: 512,
  zoomOffset: -1,
  maxZoom: 19,
  maxNativeZoom: 19,
};

/* Mapbox's terms require their wordmark and this attribution to stay visible. */
const MAPBOX_ATTRIBUTION =
  '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
/** Imagery carries its own credit on top of the above. */
const MAPBOX_SATELLITE_ATTRIBUTION = '&copy; <a href="https://www.maxar.com/">Maxar</a> | ' + MAPBOX_ATTRIBUTION;

const STADIA_API_KEY = (import.meta.env.VITE_STADIA_API_KEY as string | undefined) ?? '';
const stadiaTileUrl = (style: string, ext: 'png' | 'jpg') =>
  `https://tiles.stadiamaps.com/tiles/${style}/{z}/{x}/{y}{r}.${ext}` +
  (STADIA_API_KEY ? `?api_key=${STADIA_API_KEY}` : '');

const STADIA_TILE_OPTIONS: L.TileLayerOptions = { maxZoom: 19, maxNativeZoom: 19 };

const STADIA_ATTRIBUTION =
  '&copy; <a href="https://www.stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
/** Alidade Satellite carries its own imagery credits on top of the above. */
const STADIA_SATELLITE_ATTRIBUTION =
  '&copy; CNES, Distribution Airbus DS, &copy; Airbus DS, &copy; PlanetObserver (Contains Copernicus Data) | ' +
  STADIA_ATTRIBUTION;

interface BasemapSpec {
  url: string;
  options: L.TileLayerOptions;
}

/** Which provider actually renders. Read `usingMapbox` for the answer. */
export const usingMapbox = MAPBOX_TOKEN !== '';

const BASEMAPS: Record<BaseLayerId, BasemapSpec> = usingMapbox
  ? {
      street: {
        url: mapboxTileUrl(MAPBOX_STREET_STYLE),
        options: { ...MAPBOX_TILE_OPTIONS, attribution: MAPBOX_ATTRIBUTION },
      },
      satellite: {
        url: mapboxTileUrl(MAPBOX_SATELLITE_STYLE),
        options: { ...MAPBOX_TILE_OPTIONS, attribution: MAPBOX_SATELLITE_ATTRIBUTION },
      },
    }
  : {
      street: {
        url: stadiaTileUrl('alidade_smooth', 'png'),
        options: { ...STADIA_TILE_OPTIONS, attribution: STADIA_ATTRIBUTION },
      },
      satellite: {
        url: stadiaTileUrl('alidade_satellite', 'jpg'),
        options: { ...STADIA_TILE_OPTIONS, attribution: STADIA_SATELLITE_ATTRIBUTION },
      },
    };

/*
 * No separate labels overlay. Alidade Satellite is already a hybrid — it ships
 * roads, street names and place names baked into the imagery tiles — so adding
 * one produced two competing sets of labels on the same map.
 */

/*
 * The parcel is drawn in the single accent, #7197d3 (--survey in tokens.css).
 * Leaflet needs literal colours here, so this is the one place the accent is
 * hardcoded outside CSS — keep it in step with the token. The stroke is a touch
 * heavier and darker-on-hover than the fill to stay legible where the grey
 * imagery beneath runs light.
 */
const ACCENT = '#7197d3';

const PARCEL_STYLE: L.PathOptions = {
  color: ACCENT,
  weight: 3,
  opacity: 1,
  fillColor: ACCENT,
  fillOpacity: 0.18,
};

const DRAW_GUIDE_STYLE: L.PathOptions = {
  color: ACCENT,
  weight: 2,
  dashArray: '6 5',
};

export const ParcelMap = forwardRef<ParcelMapHandle, ParcelMapProps>(function ParcelMap(
  { initialPolygon, onPolygonChange, onModeChange },
  ref,
) {
  const t = useT();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const parcelRef = useRef<L.Polygon | null>(null);
  /* Captured once. The map effect is mount-only and must not re-run when the
     parent re-renders with a new object identity for the same parcel. */
  const initialPolygonRef = useRef(initialPolygon);
  const searchPinRef = useRef<L.CircleMarker | null>(null);
  /* Satellite is a group (imagery + reference layers); street is a single tile
     layer. Both are Layers, which is all the basemap switch below needs. */
  const layersRef = useRef<Record<BaseLayerId, L.Layer> | null>(null);

  /* Streets first: names and road shapes are how an owner finds their land.
     They switch to satellite once they are in the right place. */
  const [baseLayer, setBaseLayer] = useState<BaseLayerId>('street');

  // Callbacks are held in refs so the map effect can stay mount-only. Rebuilding
  // a Leaflet instance because a parent re-rendered would destroy the drawing.
  const onPolygonChangeRef = useRef(onPolygonChange);
  const onModeChangeRef = useRef(onModeChange);
  useEffect(() => {
    onPolygonChangeRef.current = onPolygonChange;
    onModeChangeRef.current = onModeChange;
  });

  const emitPolygon = useCallback((layer: L.Polygon | null) => {
    if (!layer) {
      onPolygonChangeRef.current(null);
      return;
    }
    // Leaflet nests rings; the exterior ring is the first element.
    const rings = layer.getLatLngs() as unknown as L.LatLng[][];
    const ring = Array.isArray(rings[0]) ? rings[0] : (rings as unknown as L.LatLng[]);
    onPolygonChangeRef.current(polygonFromLatLngs(ring ?? []));
  }, []);

  /** Wire a freshly created polygon: track every edit gesture Geoman can emit. */
  const attachParcel = useCallback(
    (layer: L.Polygon) => {
      parcelRef.current = layer;
      layer.setStyle(PARCEL_STYLE);

      const update = () => emitPolygon(layer);

      // pm:markerdrag fires continuously during a vertex drag, which is what makes
      // the area readout tick live rather than only settling on release.
      layer.on('pm:markerdrag', update);
      layer.on('pm:markerdragend', update);
      layer.on('pm:drag', update);
      layer.on('pm:dragend', update);
      layer.on('pm:edit', update);
      layer.on('pm:vertexadded', update);
      layer.on('pm:vertexremoved', update);
      layer.on('pm:cut', update);
      layer.on('remove', () => {
        if (parcelRef.current === layer) {
          parcelRef.current = null;
          onPolygonChangeRef.current(null);
          onModeChangeRef.current('idle');
        }
      });

      update();
    },
    [emitPolygon],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || mapRef.current) return;

    const map = L.map(container, {
      center: ROMANIA_CENTER,
      zoom: ROMANIA_DEFAULT_ZOOM,
      minZoom: 6,
      maxZoom: 19,
      zoomControl: false,
      attributionControl: true,
      /*
       * Wheel zoom, on request. Leaflet only receives wheel events while the
       * cursor is over the canvas, so this is hover-to-zoom by nature.
       *
       * The trade-off is real and was the reason this started out disabled: the
       * map is a tall element in the middle of a scrolling page, so scrolling
       * down past it with the cursor over the canvas zooms the map instead of
       * moving the page. Enabled deliberately at Mario-Dan's request. If it
       * proves annoying in use, the usual remedies are to require Ctrl/Cmd, or
       * to enable it only after the owner clicks the map once.
       */
      scrollWheelZoom: true,
      maxBounds: L.latLngBounds(ROMANIA_BOUNDS).pad(0.4),
      maxBoundsViscosity: 0.7,
    });
    mapRef.current = map;

    const satellite = L.tileLayer(BASEMAPS.satellite.url, BASEMAPS.satellite.options);
    const street = L.tileLayer(BASEMAPS.street.url, BASEMAPS.street.options);
    layersRef.current = { satellite, street };
    // Must match the initial `baseLayer` state above, or the first render shows
    // one basemap while the toggle claims the other.
    street.addTo(map);

    map.attributionControl.setPrefix('');

    map.pm.setGlobalOptions({
      snappable: true,
      snapDistance: 20,
      // A self-intersecting ring has no meaningful area. Blocking it at draw
      // time beats explaining a nonsense number afterwards.
      allowSelfIntersection: false,
      templineStyle: DRAW_GUIDE_STYLE,
      hintlineStyle: DRAW_GUIDE_STYLE,
      pathOptions: PARCEL_STYLE,
      // Geoman's own toolbar is suppressed; ours lives outside the canvas so it
      // can be large enough to hit on a phone.
      hideMiddleMarkers: false,
    });

    map.on('pm:create', (event) => {
      const layer = event.layer as L.Polygon;
      // One parcel at a time: a new draw replaces whatever came before.
      if (parcelRef.current && parcelRef.current !== layer) {
        parcelRef.current.remove();
        parcelRef.current = null;
      }
      attachParcel(layer);
      map.pm.disableDraw();
      layer.pm.enable({ allowSelfIntersection: false });
      onModeChangeRef.current('editing');
    });

    map.on('pm:drawstart', () => onModeChangeRef.current('drawing'));
    map.on('pm:drawend', () => {
      // pm:drawend also fires when a shape completes; pm:create runs after and
      // corrects the mode to 'editing'.
      if (!parcelRef.current) onModeChangeRef.current('idle');
    });

    // Leaflet mis-measures a container that was hidden or mid-transition at init.
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(container);

    if (import.meta.env.DEV) {
      (window as unknown as { __parcelMap?: L.Map }).__parcelMap = map;
    }

    /*
     * Redraw a parcel the owner already has. Step 1 unmounts whenever they move
     * to step 2, taking the Leaflet instance with it, so without this their
     * outline is gone when they come back.
     */
    const saved = initialPolygonRef.current;
    const ring = saved?.geometry.coordinates[0];
    if (ring && ring.length >= 4) {
      // GeoJSON is [lng, lat]; Leaflet wants [lat, lng].
      const latlngs = ring.slice(0, -1).map(([lng, lat]) => [lat, lng] as L.LatLngTuple);
      const restored = L.polygon(latlngs);
      restored.addTo(map);
      attachParcel(restored);
      // Same call pm:create makes on a freshly drawn parcel. Without it the
      // outline comes back but has no vertex handles, so the hint under the map
      // tells the owner to drag points that are not there.
      restored.pm.enable({ allowSelfIntersection: false });
      map.fitBounds(restored.getBounds(), { padding: [24, 24], maxZoom: 17, animate: false });
      onModeChangeRef.current('editing');
    }

    return () => {
      observer.disconnect();
      /*
       * Detach the parcel's listeners BEFORE tearing the map down.
       *
       * `attachParcel` binds a `remove` handler that reports the parcel as
       * deleted. `map.remove()` removes every layer, which fired it — so simply
       * navigating to step 2 told the wizard the owner had erased their land,
       * and the saved selection was wiped along with the drawing. Unmounting a
       * component must not look like a deletion.
       */
      parcelRef.current?.off();
      map.remove();
      mapRef.current = null;
      parcelRef.current = null;
      layersRef.current = null;
    };
  }, [attachParcel]);

  /*
   * Geoman draws its own tooltips onto the canvas and defaults to English. This
   * lives in its own effect rather than the map-creation effect above, which is
   * mount-only by design — re-running that on a dictionary change would destroy
   * a parcel the owner had already drawn.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.pm.setLang(
      t.meta.geomanLang as Parameters<typeof map.pm.setLang>[0],
      { tooltips: { ...t.step1.mapTooltips } },
      'en',
    );
  }, [t]);

  // Swap the basemap without touching the drawing.
  useEffect(() => {
    const map = mapRef.current;
    const layers = layersRef.current;
    if (!map || !layers) return;

    const next = layers[baseLayer];
    const other = layers[baseLayer === 'satellite' ? 'street' : 'satellite'];
    if (map.hasLayer(other)) map.removeLayer(other);
    if (!map.hasLayer(next)) next.addTo(map);
    // The parcel must stay above whichever tiles are underneath.
    parcelRef.current?.bringToFront();
  }, [baseLayer]);

  useImperativeHandle(
    ref,
    (): ParcelMapHandle => ({
      startDraw: () => {
        const map = mapRef.current;
        if (!map) return;
        if (parcelRef.current) {
          parcelRef.current.remove();
          parcelRef.current = null;
          onPolygonChangeRef.current(null);
        }
        map.pm.enableDraw('Polygon');
        onModeChangeRef.current('drawing');
      },
      stopDraw: () => {
        mapRef.current?.pm.disableDraw();
        onModeChangeRef.current(parcelRef.current ? 'editing' : 'idle');
      },
      startEdit: () => {
        parcelRef.current?.pm.enable({ allowSelfIntersection: false });
        if (parcelRef.current) onModeChangeRef.current('editing');
      },
      stopEdit: () => {
        parcelRef.current?.pm.disable();
        if (parcelRef.current) onModeChangeRef.current('idle');
      },
      reset: () => {
        const map = mapRef.current;
        if (!map) return;
        map.pm.disableDraw();
        if (parcelRef.current) {
          parcelRef.current.remove();
          parcelRef.current = null;
        }
        onPolygonChangeRef.current(null);
        onModeChangeRef.current('idle');
      },
      flyToResult: (result) => {
        const map = mapRef.current;
        if (!map) return;

        if (searchPinRef.current) {
          searchPinRef.current.remove();
          searchPinRef.current = null;
        }

        // Leaflet's fly* animations are a long camera move across the country.
        // For anyone who asked the OS for less motion, jump instead of flying.
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (result.boundingBox) {
          const [south, north, west, east] = result.boundingBox;
          const bounds = L.latLngBounds([south, west], [north, east]);
          // Cap the zoom: a bounding box for a whole commune would otherwise
          // land the owner too far out to see field boundaries.
          const fit = { maxZoom: 17, padding: [24, 24] as L.PointTuple };
          if (reduceMotion) {
            map.fitBounds(bounds, { ...fit, animate: false });
          } else {
            map.flyToBounds(bounds, { ...fit, duration: 0.8 });
          }
        } else if (reduceMotion) {
          map.setView([result.lat, result.lon], 16, { animate: false });
        } else {
          map.flyTo([result.lat, result.lon], 16, { duration: 0.8 });
        }

        // A plain circle marker, not L.marker: Leaflet's default icon needs
        // bundler-specific asset paths, and a survey dot suits the register.
        searchPinRef.current = L.circleMarker([result.lat, result.lon], {
          radius: 6,
          color: '#ffffff',
          weight: 2,
          fillColor: ACCENT,
          fillOpacity: 1,
          interactive: false,
        }).addTo(map);
      },
      invalidate: () => mapRef.current?.invalidateSize(),
    }),
    [],
  );

  const zoomBy = (delta: number) => {
    const map = mapRef.current;
    if (!map) return;
    map.setZoom(map.getZoom() + delta);
  };

  return (
    <div className="parcel-map">
      <div
        ref={containerRef}
        className="parcel-map__canvas"
        role="application"
        aria-label={t.a11y.mapLabel}
      />

      <div className="parcel-map__layer-switch">
        <fieldset>
          <legend className="visually-hidden">{t.step1.layerLabel}</legend>
          {(['street', 'satellite'] as const).map((id) => (
            <label key={id} className="layer-option">
              <input
                type="radio"
                name="parcel-base-layer"
                value={id}
                checked={baseLayer === id}
                onChange={() => setBaseLayer(id)}
              />
              <span>{id === 'satellite' ? t.step1.layerSatellite : t.step1.layerStreet}</span>
            </label>
          ))}
        </fieldset>
      </div>

      <div className="parcel-map__zoom">
        <button type="button" className="zoom-btn" onClick={() => zoomBy(1)} aria-label={t.step1.zoomIn}>
          <span aria-hidden="true">+</span>
        </button>
        <button type="button" className="zoom-btn" onClick={() => zoomBy(-1)} aria-label={t.step1.zoomOut}>
          <span aria-hidden="true">−</span>
        </button>
      </div>
    </div>
  );
});
